/* eslint-disable max-lines */
import { Slider } from '@hakimo-ui/hakimo/ui-elements';
import { usePageLayoutVariant } from '@hakimo-ui/hakimo/ui-layout';
import {
  Button,
  HakimoSpinner,
  ToggleButton,
  Tooltip,
} from '@hakimo-ui/shared/ui-base';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import {
  BackwardIcon,
  ForwardIcon,
  PauseIcon,
  PlayIcon,
} from '@heroicons/react/24/solid';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  useAlarmGroupPlayback,
  useAlarmGroupStatic,
  useAlarmGroupTime,
} from '../alarm-provider/AlarmGroupProvider';
import { useAlarmGroupTracking } from '../useAlarmGroupTracking';
import NeighbourAlarmsActions from './NeighbourAlarmsActions';
import { createMultiClickSeekState, registerMultiClickSeek } from './utils';

interface Props {
  toggleShowMinorAlarms?: (val: boolean) => void;
  showMinorAlarms?: boolean;
  onResetZoom?: () => void;
  isFetchingNeighbours?: boolean;
}

export function TimelineActions(props: Props) {
  const {
    toggleShowMinorAlarms,
    showMinorAlarms,
    onResetZoom,
    isFetchingNeighbours,
  } = props;

  const { location, isScanLike } = useAlarmGroupStatic();
  const track = useAlarmGroupTracking();
  const trackPlayback = (action: 'rew' | 'fwd' | 'reset') =>
    track('alarm_group_playback_control', { action });
  const isSplitLayout = usePageLayoutVariant() === 'split';
  const {
    isSeekerPaused,
    toggleSeekerPaused,
    playbackSpeed,
    updatePlaybackSpeed,
    seekerTimestamp,
    seekTo,
  } = useAlarmGroupTime();
  const { allCombinedAlarms, resetTimeline, timelineBounds } =
    useAlarmGroupPlayback();

  const seekBy = (deltaMs: number) => {
    const { totalTimelineStartTime, totalTimelineEndTime } = timelineBounds;
    const next = Math.max(
      totalTimelineStartTime,
      Math.min(totalTimelineEndTime, seekerTimestamp + deltaMs)
    );
    seekTo(next);
  };

  const multiClickRef = useRef(createMultiClickSeekState());
  const [seekFeedback, setSeekFeedback] = useState<{
    direction: 1 | -1;
    ms: number;
  } | null>(null);

  useEffect(() => {
    return () => {
      if (multiClickRef.current.timer) {
        clearTimeout(multiClickRef.current.timer);
      }
    };
  }, []);

  const handleSeekClick = (direction: 1 | -1) => {
    trackPlayback(direction === -1 ? 'rew' : 'fwd');
    registerMultiClickSeek(multiClickRef.current, direction, seekBy, () =>
      setSeekFeedback(null)
    );
    setSeekFeedback({ direction, ms: multiClickRef.current.appliedMs });
  };

  const getSeekTooltipText = (direction: 1 | -1) => {
    if (seekFeedback?.direction === direction) {
      const seconds = seekFeedback.ms / 1000;
      return direction === -1 ? `❮\u00A0-${seconds}` : `+${seconds}\u00A0❯`;
    }
    return direction === -1 ? 'REW' : 'FWD';
  };

  const timezone = location?.timezone ?? 'UTC';
  const formatClockTime = (ts: number) =>
    new Date(ts).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZone: timezone,
    });
  const formatClockDate = (ts: number) => {
    const parts = new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: '2-digit',
      timeZone: timezone,
    }).formatToParts(new Date(ts));
    const day = parts.find((p) => p.type === 'day')?.value ?? '';
    const month = parts.find((p) => p.type === 'month')?.value ?? '';
    const year = parts.find((p) => p.type === 'year')?.value ?? '';
    return `${day}-${month}-${year}`;
  };

  const [criticalAlarmsCount, minorAlarmsCount] = useMemo(() => {
    let criticalCount = 0;
    let minorCount = 0;
    // Severity marks minors on scan-shaped data, resolved-state on RG video.
    // `isScanLike`, not `groupHasFrames`: it is `product === SCAN` on v2 and so
    // correct from the first render, before media arrives.
    for (const alarm of allCombinedAlarms) {
      const isMinor = isScanLike
        ? alarm.severity === 'low'
        : alarm.resolvedAlarm;
      if (isMinor) {
        minorCount++;
      } else {
        criticalCount++;
      }
    }
    return [criticalCount, minorCount];
  }, [allCombinedAlarms, isScanLike]);

  return (
    <div className="ml-2 flex items-center gap-2">
      <div className="flex flex-1 items-center">
        <Tooltip text="Current Alarm Timeline" position="top" size="medium">
          <span className="text-onlight-text-1 dark:text-ondark-text-1 flex items-center gap-2 whitespace-nowrap text-xs tabular-nums">
            <span className="text-onlight-text-2 dark:text-ondark-text-2">
              {formatClockDate(timelineBounds.alarmGroupStartTime)}
            </span>
            <span className="text-onlight-text-1 dark:text-ondark-text-1">
              {formatClockTime(timelineBounds.alarmGroupStartTime)} -{' '}
              {formatClockTime(timelineBounds.alarmGroupEndTime)}
            </span>
          </span>
        </Tooltip>
      </div>

      {isFetchingNeighbours && (
        <Tooltip text="Loading neighbouring alarms" position="top">
          <span className="inline-flex items-center">
            <HakimoSpinner />
          </span>
        </Tooltip>
      )}

      <div className="border-onlight-line-2 dark:border-ondark-line-2 flex items-center gap-0.5 rounded-md border px-1">
        <div className="flex items-center">
          <NeighbourAlarmsActions disabled={isFetchingNeighbours} />
        </div>

        <div className="flex items-center">
          <Tooltip text="Reset Timeline" position="top">
            <Button
              variant="icon"
              onClick={() => {
                trackPlayback('reset');
                resetTimeline();
                onResetZoom?.();
              }}
              disabled={isFetchingNeighbours}
            >
              <ArrowPathIcon className="h-5 w-5" />
            </Button>
          </Tooltip>
        </div>

        <div className="flex items-center">
          <Tooltip
            text={getSeekTooltipText(-1)}
            position="top"
            visible={seekFeedback?.direction === -1}
          >
            <Button variant="icon" onClick={() => handleSeekClick(-1)}>
              <BackwardIcon className="h-5 w-5" />
            </Button>
          </Tooltip>
          <Tooltip text={isSeekerPaused ? 'Play' : 'Pause'} position="top">
            <Button variant="icon" onClick={toggleSeekerPaused}>
              {isSeekerPaused ? (
                <PlayIcon className="h-5 w-5" />
              ) : (
                <PauseIcon className="h-5 w-5" />
              )}
            </Button>
          </Tooltip>
          <Tooltip
            text={getSeekTooltipText(1)}
            position="top"
            visible={seekFeedback?.direction === 1}
          >
            <Button variant="icon" onClick={() => handleSeekClick(1)}>
              <ForwardIcon className="h-5 w-5" />
            </Button>
          </Tooltip>
        </div>

        <Slider
          initialValue={playbackSpeed}
          minValue={0.5}
          maxValue={4}
          step={0.5}
          label="Speed"
          labelClassName={isSplitLayout ? 'hidden 2xl:flex' : ''}
          onValueChange={updatePlaybackSpeed}
        />
      </div>

      <div className="flex flex-1 items-center justify-end gap-2">
        <span
          className={`text-onlight-text-2 dark:text-ondark-text-2 whitespace-nowrap text-sm ${
            isSplitLayout ? 'hidden 2xl:inline' : ''
          }`}
        >
          Alarm Types
        </span>
        <ToggleButton
          activeColor="earth-500"
          inactiveColor="earth-500"
          isActive
          disabled
          size="small"
          title={`${criticalAlarmsCount} Critical`}
          text={`${criticalAlarmsCount} Critical`}
        />
        <ToggleButton
          activeColor="sage-500"
          inactiveColor="sage-400"
          isActive={!!showMinorAlarms}
          onToggle={toggleShowMinorAlarms}
          size="small"
          title={`${minorAlarmsCount} Minor`}
          text={`${minorAlarmsCount} Minor`}
        />
      </div>
    </div>
  );
}

export default TimelineActions;
